import { useQuery } from '@tanstack/react-query'
import { MousePointer, ExternalLink, TrendingUp } from 'lucide-react'
import { seoAnalytics } from '@/lib/api'

interface AdsCampaignsTableProps {
  days: number
  section?: string
}

export default function AdsCampaignsTable({ days, section }: AdsCampaignsTableProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['seo-ads-campaigns', days, section],
    queryFn: () => seoAnalytics.getAdsCampaigns(days, 50, section),
  })

  const campaigns = data?.data || []

  // Calculate totals
  const totalVisits = campaigns.reduce((sum: number, c: any) => sum + (c.visits || 0), 0)
  const totalUnique = campaigns.reduce((sum: number, c: any) => sum + (c.unique_ips || 0), 0)

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30">
            <MousePointer className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Campanas Google Ads</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Trafico por UTM campaign
            </p>
          </div>
        </div>

        {/* Totals */}
        <div className="flex items-center gap-4 text-sm">
          <div>
            <span className="text-slate-500">Total clicks: </span>
            <span className="font-medium text-slate-900 dark:text-white">
              {totalVisits.toLocaleString()}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Unicos: </span>
            <span className="font-medium text-slate-900 dark:text-white">
              {totalUnique.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <div className="card-body p-0">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse text-slate-400">Cargando datos...</div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <MousePointer className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay datos de campanas Google Ads</p>
            <p className="text-sm mt-1">Los clicks con gclid/utm aparecen aqui</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Campana
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Fuente / Medio
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Clicks
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Unicos
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    % del Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {campaigns.map((campaign: any, idx: number) => {
                  const percent = totalVisits > 0
                    ? Math.round((campaign.visits / totalVisits) * 100)
                    : 0
                  return (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium text-slate-900 dark:text-white">
                            {campaign.utm_campaign || '(sin nombre)'}
                          </span>
                        </div>
                        {campaign.utm_content && (
                          <div className="text-xs text-slate-400 mt-0.5 ml-6">
                            {campaign.utm_content}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {campaign.utm_source || 'google'} / {campaign.utm_medium || 'cpc'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {campaign.visits?.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {campaign.unique_ips?.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-amber-500"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <span className="text-sm text-slate-600 dark:text-slate-400 w-8">
                            {percent}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
